<!--
  print-fallback-modal.component.html

  A settings screen, shaped like one: a title, a line saying what is being set,
  and ONE CARD PER DOCUMENT — labels, copier sheets — each a two-way choice
  between arriving on its own and having to be printed from a desktop.

  Two cards rather than four whole-job arrangements because the operator's event
  is about one printer: the Zebra is jammed, or the copier is out. A facility
  waiting on its label printer sits in one of them permanently, and nothing is
  wrong there, so a card whose automatic side its hardware cannot back up shows
  the reason beside the disabled control rather than hiding it.

  There is no read-only "where this location prints" block above the cards any
  more. It restated what the cards and their help already say — which printer,
  and whether it arrives on its own — and a settings screen that opens with a
  paragraph of facts before its first control buries the control.

  Built from the app's existing furniture: the `.card-popup` shell from
  styles.css, the eyebrow section label and read-only summary block the reprint
  modal uses, the global `.advisory` family for anything washed, and
  `.submit-btn` from buttons.css in the footer. Nothing here overrides a global
  rule.
-->

<ion-modal [isOpen]="isOpen" (didDismiss)="onClose()" class="card-popup">
  <ng-template>

    <ion-header>
      <ion-toolbar>
        <ion-title>Print Settings</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <div class="print-fallback-modal-body">

        <!-- The settings-page description line, and the one fact the cards cannot
             show: this is set here, on this device, and nowhere else. It replaces
             the "Right now" heading — a lone heading over the only section on a
             screen labels nothing. -->
        <p class="print-intro">
          How each document prints from this device, at {{ locationLabel }}.
        </p>

        <!-- One card and one radio group per document. Each is bound to its row's
             `selected`, which is read off effectiveMode rather than the stored
             mode: at a facility with no shop label printer the stored mode is "no
             override", but its labels come off a Dymo, and a row reading "sent
             automatically" would describe a printer that is not installed. -->
        <div class="print-doc-card" *ngFor="let row of rows">

          <div class="print-doc-header">
            <span class="print-doc-title">{{ row.title }}</span>
            <ion-button
              fill="clear"
              size="small"
              class="print-doc-info-btn"
              [attr.aria-expanded]="openInfoDoc === row.doc"
              [attr.aria-label]="'About ' + row.title + ' printing'"
              (click)="onToggleInfo(row)">
              <!-- One fixed icon, highlighted by class when its help is open.
                   Swapping `name` made Ionic lazy-fetch the other icon's SVG on
                   every toggle, and a fetch that fails (a restarting dev server,
                   a dropped connection) surfaces as an unhandled "TypeError:
                   Failed to fetch" from this template. Nothing here needs a
                   second icon file to say "open". -->
              <ion-icon
                slot="icon-only"
                name="information-circle-outline"
                [class.print-doc-info-open]="openInfoDoc === row.doc">
              </ion-icon>
            </ion-button>
          </div>

          <ion-radio-group
            class="print-doc-choices"
            [value]="row.selected"
            (ionChange)="onSelectRow(row.doc, $event.detail.value)">

            <ion-radio
              class="print-doc-choice"
              value="auto"
              [disabled]="!!row.unavailableReason"
              labelPlacement="end"
              justify="start">
              Sent automatically
            </ion-radio>

            <ion-radio
              class="print-doc-choice"
              value="desktop"
              labelPlacement="end"
              justify="start">
              Printed from a desktop computer only
            </ion-radio>

          </ion-radio-group>

          <!-- Why the automatic side cannot be chosen here. On the screen, never
               behind the info button: a greyed-out control whose explanation is a
               tap away is a dead control to whoever was sent here to turn it on. -->
          <div class="advisory advisory-compact advisory-info print-doc-unavailable"
               *ngIf="row.unavailableReason">
            <ion-icon name="information-circle-outline" aria-hidden="true"></ion-icon>
            <span>{{ row.unavailableReason }}</span>
          </div>

          <!-- The help text, in place rather than in a second modal: the choice it
               describes stays on screen and under the operator's thumb while they
               read it. -->
          <div class="print-doc-info" *ngIf="openInfoDoc === row.doc">
            <div class="print-doc-info-heading">What this does</div>
            <p class="print-doc-info-text">
              <strong>Sent automatically</strong> — {{ row.info.automatic }}
            </p>
            <p class="print-doc-info-text">
              <strong>Printed from a desktop computer only</strong> — {{ row.info.desktopOnly }}
            </p>

            <div class="print-doc-info-heading">When to use it</div>
            <p class="print-doc-info-text">{{ row.info.whenToUse }}</p>
            <p class="print-doc-info-text">{{ row.info.otherDoc }}</p>
          </div>

        </div>

        <!-- The phone caveat, visible and never behind the info button: this is
             the sentence that stops someone standing at a phone waiting for paper
             that is not coming. -->
        <div class="advisory advisory-compact advisory-warning print-fallback-waiting"
             *ngIf="phoneWaitingNote">
          <ion-icon name="phone-portrait-outline" aria-hidden="true"></ion-icon>
          <span>{{ phoneWaitingNote }}</span>
        </div>

        <!-- Which of the two things the selection above is. Without this, an
             operator cannot tell a permanent arrangement from a switch someone
             flipped an hour ago, and the second one is the one worth undoing. -->
        <p class="print-fallback-note" *ngIf="!isOverride">
          This is how {{ locationLabel }} is set up to print. Nothing here has been
          switched to desktop-only.
        </p>

        <!-- Stated up front, so the expiry is never a surprise mid-shift. The
             setting is browser storage on ONE device: it is not a report, nobody
             else sees it, and the other device in the building still prints the
             way it always did. -->
        <p class="print-fallback-note" *ngIf="isOverride">
          These print settings are set for this device only and will reset after 12
          hours. They change nothing for anyone else, on any other phone or computer.
          If the printer is still down tomorrow, set them again. If it will be down
          longer than that, ask for {{ locationLabel }}'s setup to be changed instead.
        </p>

      </div>
    </ion-content>

    <ion-footer>
      <ion-toolbar>
        <ion-buttons slot="end">
          <!-- Closes only. Each choice is applied the moment it is selected —
               see onSelectRow() — so there is nothing here left to commit. -->
          <ion-button (click)="onClose()" class="submit-btn">
            <ion-icon name="checkmark" slot="start"></ion-icon>
            &nbsp;Done
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-footer>

  </ng-template>
</ion-modal>
